import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LifeBuoy, AlertTriangle, ShieldCheck, Flag, Lock, Search, Loader2, ChevronDown } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export default function SafetyCenter() {
  const [content, setContent] = useState(null);
  const [q, setQ] = useState("");
  const [openFaq, setOpenFaq] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [form, setForm] = useState({ category: "safety", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const loadTickets = () => api.get("/support/tickets").then(({ data }) => setTickets(data.tickets)).catch(() => {});
  useEffect(() => {
    api.get("/safety/content").then(({ data }) => setContent(data)).catch(() => setContent({ articles: [], faqs: [], notices: [] }));
    loadTickets();
  }, []);

  const submit = async () => {
    if (!form.subject || !form.message) return toast.error("Please fill subject and message");
    setBusy(true);
    try { await api.post("/support/tickets", form); toast.success("Support ticket created"); setForm({ ...form, subject: "", message: "" }); loadTickets(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!content) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#781826]" /></div>;
  const faqs = content.faqs.filter((f) => !q || `${f.title} ${f.body}`.toLowerCase().includes(q.toLowerCase()));
  const articles = content.articles.filter((a) => !q || `${a.title} ${a.body}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="safety-center-page">
      <div><h1 className="font-serif text-3xl font-bold text-[#58111A] flex items-center gap-2"><LifeBuoy className="w-6 h-6 text-[#C59B27]" /> Safety Center</h1>
        <p className="text-sm text-[#605255]">Practical guidance to keep your search safe. Your safety always comes first.</p></div>

      {content.notices?.map((n) => (
        <div key={n.id} className="flex items-start gap-3 rounded-xl bg-[#FEF3C7] border border-[#FDE68A] p-4 text-sm text-[#92400E]" data-testid={`safety-notice-${n.id}`}>
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" /><div><strong>{n.title}</strong><div>{n.body}</div></div>
        </div>
      ))}

      <div className="flex items-start gap-3 rounded-xl bg-[#FEF2F2] border border-[#FECACA] p-4 text-sm text-[#991B1B]">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
        <span><strong>Never send money to someone you met online.</strong> Never share OTPs or passwords. Report any request for money or suspicious behaviour immediately.</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[["Report a profile", Flag, "/app/matches"], ["Privacy Settings", Lock, "/app/settings"], ["Verification", ShieldCheck, "/app/verification"], ["Contact Support", LifeBuoy, "#support"]].map(([l, Icon, to]) => (
          <Link key={l} to={to} data-testid={`safety-action-${l.split(" ")[0].toLowerCase()}`} className="mj-card p-3 flex flex-col items-center gap-1.5 text-center hover:shadow-md transition">
            <Icon className="w-5 h-5 text-[#781826]" /><span className="text-xs font-medium text-[#261A1C]">{l}</span>
          </Link>
        ))}
      </div>

      <div className="flex items-center gap-2 bg-white border border-[#E8DFD1] rounded-xl px-3">
        <Search className="w-4 h-4 text-[#C59B27]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search safety topics & FAQs" data-testid="safety-search" className="flex-1 py-2.5 text-sm outline-none" />
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {articles.map((a) => (
          <div key={a.id} className="mj-card p-4" data-testid={`safety-article-${a.id}`}>
            <div className="font-semibold text-sm text-[#261A1C] mb-1 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#C59B27]" /> {a.title}</div>
            <div className="text-xs text-[#605255] leading-relaxed">{a.body}</div>
          </div>
        ))}
      </div>

      {faqs.length > 0 && (
        <div className="mj-card p-5" data-testid="safety-faq">
          <h2 className="font-serif text-lg font-semibold text-[#261A1C] mb-2">Frequently Asked Questions</h2>
          {faqs.map((f) => (
            <div key={f.id} className="border-b border-[#F1E9DA] last:border-0">
              <button onClick={() => setOpenFaq(openFaq === f.id ? null : f.id)} className="w-full flex items-center justify-between py-3 text-left text-sm font-medium text-[#261A1C]">
                {f.title} <ChevronDown className={`w-4 h-4 transition ${openFaq === f.id ? "rotate-180" : ""}`} />
              </button>
              {openFaq === f.id && <p className="text-xs text-[#605255] pb-3">{f.body}</p>}
            </div>
          ))}
        </div>
      )}

      <div id="support" className="mj-card p-5">
        <h2 className="font-serif text-lg font-semibold text-[#261A1C] mb-1">Contact ManaJodi Support</h2>
        <p className="text-xs text-[#9E7B1B] mb-3">Need help? Email <a href="mailto:info@vs-solutions.org" className="underline">info@vs-solutions.org</a> or raise a ticket below.</p>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <select className="mj-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            <option value="safety">Safety Support</option><option value="account">Account Support</option><option value="payment">Payment Support</option><option value="other">Other</option>
          </select>
          <input className="mj-input" placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="ticket-subject" />
        </div>
        <textarea className="mj-input" rows={3} placeholder="Describe your issue…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="ticket-message" />
        <button onClick={submit} disabled={busy} data-testid="submit-ticket" className="btn-primary mt-3">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Ticket"}</button>
      </div>

      {tickets.length > 0 && (
        <div className="mj-card p-5">
          <h2 className="font-serif text-lg font-semibold text-[#261A1C] mb-3">Your Tickets</h2>
          {tickets.map((t) => (
            <div key={t.id} className="flex items-center justify-between py-2 border-b border-[#F1E9DA] last:border-0">
              <div><div className="text-sm font-medium text-[#261A1C]">{t.subject}</div><div className="text-xs text-[#605255] capitalize">{t.category}</div></div>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-amber-700 bg-amber-50 capitalize">{t.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
